import { useState } from "react";
import { Flex } from "@jsq/ds/core";
import { DdqProdOpsWorkflowType } from "~/graphql/supergraphEnums";
import { DocumentSplitView } from "./DocumentSplitView";
import { ExtractionErrorState } from "./ExtractionErrorState";
import { ExtractionLoadingState } from "./ExtractionLoadingState";
import { HistoricalAnswers } from "./HistoricalAnswers";
import { QuestionTree } from "./QuestionTree";
import { RerunDocumentProcessBar } from "./RerunDocumentProcessBar";
import { StructureDdqModal } from "./StructureDdqModal";
import { useStructureDdqRun } from "./useStructureDdqRun";

export type StructureDdqStepProps = {
  arenaId: number;
  processTaskId: string;
  ddqId: string | null;
  workflowType: DdqProdOpsWorkflowType;
  uploadId: string | null;
  completedDdqTitle: string | null;
  onExtractionSucceeded: () => void;
};

export const StructureDdqStep = ({
  arenaId,
  processTaskId,
  ddqId,
  workflowType,
  uploadId,
  completedDdqTitle,
  onExtractionSucceeded,
}: StructureDdqStepProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const closeModal = () => setIsModalOpen(false);
  // Only BackfillDdq gets new treatment here; DdqRequest and OriginalFormat both keep the
  // pre-existing behavior unchanged, since this plan never touched them.
  const isBackfill = workflowType === DdqProdOpsWorkflowType.BackfillDdq;

  const {
    defaults,
    isLoadingDefaults,
    isRunStateKnown,
    isRunning,
    failureMessage,
    startError,
    isStarting,
    run,
  } = useStructureDdqRun({
    arenaId,
    processTaskId,
    workflowType,
    onStarted: closeModal,
    onSucceeded: onExtractionSucceeded,
  });

  if (isRunning) {
    return <ExtractionLoadingState />;
  }

  const modal = isModalOpen && (
    <StructureDdqModal
      defaults={defaults}
      isLoadingDefaults={isLoadingDefaults}
      errorMessage={startError}
      isSubmitting={isStarting}
      completedDdqTitle={completedDdqTitle}
      onHide={closeModal}
      onConfirm={run}
    />
  );

  const rerunBar = failureMessage ? (
    <ExtractionErrorState message={failureMessage} onRetry={() => setIsModalOpen(true)} />
  ) : (
    <RerunDocumentProcessBar onRerun={() => setIsModalOpen(true)} disabled={!isRunStateKnown} />
  );

  // uploadId comes from the workflow row's targetId, which is always present for a live
  // workflow — this check exists only to satisfy DocumentSplitView/HistoricalAnswers' non-nullable
  // prop types, not because this case is expected to happen.
  if (isBackfill && uploadId != null) {
    return (
      <DocumentSplitView arenaId={arenaId} documentId={uploadId}>
        <Flex flexDirection="column">
          {rerunBar}
          <HistoricalAnswers arenaId={arenaId} uploadId={uploadId} />
        </Flex>
        {modal}
      </DocumentSplitView>
    );
  }

  return (
    <Flex flexDirection="column">
      {rerunBar}
      {ddqId != null && <QuestionTree arenaId={arenaId} ddqId={ddqId} />}
      {modal}
    </Flex>
  );
};
